import { isPlatformBrowser } from '@angular/common';
import { computed, DestroyRef, effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, merge } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { DebugService } from './debug.service';

export interface OfflineStatus {
  isOnline: boolean;
  networkStrength: 'good' | 'poor' | 'offline';
  lastSyncTime: Date | null;
  pendingOperationsCount: number;
  syncInProgress: boolean;
  lastConnectionLoss: Date | null;
}

export interface OfflineCapabilities {
  orders: boolean;
  inventory: boolean;
  products: boolean;
  tables: boolean;
  payments: boolean; // Payments require special handling
  reports: boolean;
}

export interface OfflineSyncMetadata {
  entityType: 'orders' | 'products' | 'inventory' | 'tables';
  lastSync: Date;
  syncVersion: number;
  conflictCount: number;
  pendingCount: number;
}

export interface OfflineConflict {
  id: string;
  entityType: string;
  entityId: string;
  clientVersion: unknown;
  serverVersion: unknown;
  timestamp: Date;
  resolved: boolean;
}

@Injectable({ providedIn: 'root' })
export class OfflineService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #debug = inject(DebugService);
  // Core state signals
  private readonly _isOnline = signal<boolean>(true);
  private readonly _isOfflineMode = signal<boolean>(false);
  private readonly _lastSyncTime = signal<Date | undefined>(undefined);
  private readonly _pendingOperationsCount = signal<number>(0);
  private readonly _syncInProgress = signal<boolean>(false);
  private readonly _lastConnectionLoss = signal<Date | undefined>(undefined);

  // Capabilities signal
  private readonly _offlineCapabilities = signal<OfflineCapabilities>({
    orders: true,
    inventory: true,
    products: true,
    tables: true,
    payments: false, // Payments require special handling
    reports: true,
  });

  // Computed signals
  readonly status = computed<OfflineStatus>(() => ({
    isOnline: this._isOnline(),
    networkStrength: this._isOnline() ? 'good' : 'offline',
    lastSyncTime: this._lastSyncTime() || null,
    pendingOperationsCount: this._pendingOperationsCount(),
    syncInProgress: this._syncInProgress(),
    lastConnectionLoss: this._lastConnectionLoss() || null,
  }));

  readonly isOnline = this._isOnline.asReadonly();
  readonly isOfflineMode = this._isOfflineMode.asReadonly();
  readonly lastSyncTime = this._lastSyncTime.asReadonly();
  readonly pendingOperationsCount = this._pendingOperationsCount.asReadonly();
  readonly syncInProgress = this._syncInProgress.asReadonly();
  readonly offlineCapabilities = this._offlineCapabilities.asReadonly();

  // Computed states
  readonly canOperateOffline = computed(
    () => this._offlineCapabilities().orders || this._offlineCapabilities().inventory || this._offlineCapabilities().products,
  );

  readonly needsSync = computed(() => this._pendingOperationsCount() > 0 && this._isOnline() && !this._syncInProgress());

  readonly isDataStale = computed(() => {
    const lastSync = this._lastSyncTime();
    if (!lastSync) return true;

    const staleThreshold = 30 * 60 * 1000; // 30 minutes
    return Date.now() - lastSync.getTime() > staleThreshold;
  });

  constructor() {
    if (isPlatformBrowser(this.#platformId)) {
      this.initializeNetworkMonitoring();
      this.initializeEffects();
    }
  }

  private initializeNetworkMonitoring(): void {
    // Initialize with current navigator status
    this._isOnline.set(navigator.onLine);

    // Listen to network events
    const online$ = fromEvent(window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(window, 'offline').pipe(map(() => false));

    merge(online$, offline$)
      .pipe(startWith(navigator.onLine), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe((isOnline) => {
        const wasOnline = this._isOnline();
        this._isOnline.set(isOnline);

        if (!isOnline && wasOnline) {
          // Connection lost
          this._lastConnectionLoss.set(new Date());
          this.handleConnectionLoss();
        } else if (isOnline && !wasOnline) {
          // Connection restored
          this.handleConnectionRestored();
        }
      });
  }

  private initializeEffects(): void {
    // Auto-sync when coming online with pending operations
    effect(() => {
      if (this.needsSync()) {
        this.triggerSync();
      }
    });
  }

  private handleConnectionLoss(): void {
    this.#debug.logOfflineEvent('connection-lost', 'Connection lost - switching to offline mode');
    this._isOfflineMode.set(true);

    // Notify components about offline mode
    this.notifyOfflineMode();
  }

  private handleConnectionRestored(): void {
    this.#debug.logOfflineEvent('connection-restored', 'Connection restored');
    this._isOfflineMode.set(false);

    // Trigger sync if there are pending operations
    if (this._pendingOperationsCount() > 0) {
      setTimeout(() => this.triggerSync(), 1000); // Small delay to ensure stability
    }
  }

  private notifyOfflineMode(): void {
    // Could emit events or update other services
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'OFFLINE_MODE_ACTIVATED',
        timestamp: new Date().toISOString(),
      });
    }
  }

  // Public methods for managing offline state
  public setOfflineMode(enabled: boolean): void {
    this._isOfflineMode.set(enabled);
  }

  public incrementPendingOperations(): void {
    this._pendingOperationsCount.update((count) => count + 1);
  }

  public decrementPendingOperations(): void {
    this._pendingOperationsCount.update((count) => Math.max(0, count - 1));
  }

  public setPendingOperationsCount(count: number): void {
    this._pendingOperationsCount.set(Math.max(0, count));
  }

  public setSyncInProgress(inProgress: boolean): void {
    this._syncInProgress.set(inProgress);
  }

  public updateLastSyncTime(time?: Date): void {
    this._lastSyncTime.set(time || new Date());
  }

  public updateOfflineCapabilities(capabilities: Partial<OfflineCapabilities>): void {
    this._offlineCapabilities.update((current) => ({ ...current, ...capabilities }));
  }

  // Sync operations
  public async triggerSync(): Promise<void> {
    if (this._syncInProgress() || !this._isOnline()) {
      return;
    }

    this.#debug.logOfflineEvent('sync-triggered', 'Triggering data synchronization...');
    this.setSyncInProgress(true);

    try {
      // This will be implemented by the SyncService
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'TRIGGER_SYNC',
          timestamp: new Date().toISOString(),
        });
      }

      // For now, just update the sync time
      this.updateLastSyncTime();
    } catch (error) {
      console.error('❌ Sync failed:', error);
    } finally {
      this.setSyncInProgress(false);
    }
  }

  public async forceSyncAll(): Promise<void> {
    this.setPendingOperationsCount(1); // Force sync even without pending operations
    return this.triggerSync();
  }

  // Utility methods
  public canPerformOperation(operationType: keyof OfflineCapabilities): boolean {
    const capabilities = this._offlineCapabilities();
    return this._isOnline() || capabilities[operationType];
  }

  public requiresOnlineConnection(operationType: keyof OfflineCapabilities): boolean {
    const capabilities = this._offlineCapabilities();
    return !capabilities[operationType];
  }

  public getNetworkStatus(): 'online' | 'offline' | 'unstable' {
    if (!this._isOnline()) return 'offline';
    if (this._isOfflineMode()) return 'unstable';
    return 'online';
  }

  public getDataFreshness(): 'fresh' | 'stale' | 'unknown' {
    const lastSync = this._lastSyncTime();
    if (!lastSync) return 'unknown';

    const timeSinceSync = Date.now() - lastSync.getTime();
    const freshThreshold = 5 * 60 * 1000; // 5 minutes
    const staleThreshold = 30 * 60 * 1000; // 30 minutes

    if (timeSinceSync < freshThreshold) return 'fresh';
    if (timeSinceSync < staleThreshold) return 'stale';
    return 'unknown';
  }
}
